import { forwardRef, useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import type { WithClassName } from './types';

export type TabItem = { id: string; label: string; panel: ReactNode; disabledReason?: string };
export type TabsProps = WithClassName & {
  label: string;
  items: readonly TabItem[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (id: string) => void;
  activation?: 'automatic' | 'manual';
  orientation?: 'horizontal' | 'vertical';
};

export const Tabs = forwardRef<HTMLDivElement, TabsProps>(function Tabs({
  label, items, value, defaultValue, onValueChange, activation = 'automatic', orientation = 'horizontal', className,
}, ref) {
  const id = useId();
  const [localValue, setLocalValue] = useState(defaultValue);
  const enabled = items.filter((item) => !item.disabledReason);
  const requested = value ?? localValue;
  const selected = enabled.find((item) => item.id === requested)?.id ?? enabled[0]?.id;
  const [focused, setFocused] = useState<string>();
  const roving = enabled.some((item) => item.id === focused) ? focused : selected;
  const buttons = useRef(new Map<string, HTMLButtonElement>());
  const focusActivation = useRef<string>();
  const [visited, setVisited] = useState<ReadonlySet<string>>(() => new Set(selected ? [selected] : []));

  useEffect(() => {
    if (selected) setVisited((previous) => previous.has(selected) ? previous : new Set([...previous, selected]));
  }, [selected]);

  function select(next: string) {
    if (next === selected) return;
    if (value === undefined) setLocalValue(next);
    onValueChange?.(next);
  }

  function navigate(event: KeyboardEvent<HTMLButtonElement>, current: string) {
    const previousKey = orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft';
    const nextKey = orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight';
    if (!['Home', 'End', previousKey, nextKey].includes(event.key) || !enabled.length) return;
    event.preventDefault();
    const index = enabled.findIndex((item) => item.id === current);
    const target = event.key === 'Home' ? enabled[0] : event.key === 'End' ? enabled[enabled.length - 1]
      : enabled[(index + (event.key === nextKey ? 1 : -1) + enabled.length) % enabled.length];
    if (target) buttons.current.get(target.id)?.focus();
  }

  return <div ref={ref} className={`ui-tabs ui-tabs--${orientation} ${className ?? ''}`}>
    <div className="ui-tabs__list" role="tablist" aria-label={label} aria-orientation={orientation}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(undefined);
      }}>
      {items.map((item) => {
        const itemId = `${id}-${encodeURIComponent(item.id)}`;
        return <button key={item.id} ref={(node) => {
          if (node) buttons.current.set(item.id, node); else buttons.current.delete(item.id);
        }} type="button" role="tab" id={`${itemId}-tab`} className="ui-tabs__tab"
          aria-controls={`${itemId}-panel`} aria-selected={item.id === selected}
          aria-describedby={item.disabledReason ? `${itemId}-reason` : undefined}
          disabled={!!item.disabledReason} tabIndex={item.id === roving ? 0 : -1}
          onPointerDown={() => { focusActivation.current = undefined; }}
          onFocus={() => {
            setFocused(item.id);
            if (activation === 'automatic') { focusActivation.current = item.id; select(item.id); }
          }}
          onBlur={() => { focusActivation.current = undefined; }}
          onClick={() => {
            // Some browsers do not focus clicked buttons. Click must also select,
            // without duplicating the request made by this click's focus event.
            if (activation !== 'automatic' || focusActivation.current !== item.id) select(item.id);
            focusActivation.current = undefined;
          }}
          onKeyDown={(event) => navigate(event, item.id)}>{item.label}</button>;
      })}
    </div>
    {items.filter((item) => item.disabledReason).map((item) => <p key={item.id}
      id={`${id}-${encodeURIComponent(item.id)}-reason`} className="ui-tabs__reason">{item.label}: {item.disabledReason}</p>)}
    <div className="ui-tabs__panels">
      {items.map((item) => {
        const itemId = `${id}-${encodeURIComponent(item.id)}`;
        return <div key={item.id} id={`${itemId}-panel`} role="tabpanel" aria-labelledby={`${itemId}-tab`}
          className="ui-tabs__panel" tabIndex={0} hidden={item.id !== selected}>
          {item.id === selected || visited.has(item.id) ? item.panel : null}
        </div>;
      })}
    </div>
  </div>;
});
